import { useState, ReactNode } from 'react'
import { ContextMenuBaseProps, ContextMenuItem, ContextMenu } from 'features/common/ui/ContextMenu'
import { ExpandMoreOutlined as ExpandIcon, ExpandLessOutlined as FoldUpIcon } from '@mui/icons-material'
import { useMediaQuery, Theme, styled } from '@mui/material'

const Menu = styled(ContextMenu, {
  shouldForwardProp: (prop) => prop !== 'isOpened' && prop !== 'color'
})<{
  isOpened?: boolean
  color?: 'primary' | 'secondary'
}>(({ isOpened, theme, color }) => ({
  '& button': {
    color: color === 'secondary' ? theme.palette.text.secondary : theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },

    ...(isOpened && {
      backgroundColor: theme.palette.action.hover
    })
  }
}))

export interface FilterDropdownItem extends ContextMenuItem {
  alternativeLabel?: string
}

export interface FilterDropdownProps extends ContextMenuBaseProps {
  value?: FilterDropdownItem
  defaultValue?: FilterDropdownItem
  disabled?: boolean
  buttonSize?: 'small' | 'medium' | 'large'
  icon?: ReactNode
  label?: ReactNode
  ariaLabel?: string
  responsive?: boolean
  useDefaultValue?: boolean
  color?: 'primary' | 'secondary'
}

export function FilterDropdown({
  menuItems,
  onClickItem,
  value,
  defaultValue = menuItems[0],
  menuTitle,
  disabled,
  buttonSize,
  icon,
  dense,
  label,
  ariaLabel,
  responsive,
  useDefaultValue = true,
  placement = 'bottom-end',
  color
}: FilterDropdownProps) {
  const [open, setOpen] = useState<boolean>(false)
  const [selected_, setSelected] = useState<FilterDropdownItem | undefined>(useDefaultValue ? defaultValue : undefined)

  const selected = value ?? selected_
  const handleChange = (item: ContextMenuItem) => {
    setSelected(item)
    onClickItem(item)
    setOpen(false)
  }

  const isOpen = (open: boolean) => {
    setOpen(open)
  }

  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
  const responsiveIcon = isTablet && icon
  const menuLabel = (responsive ? responsiveIcon : icon) || label || selected?.alternativeLabel || selected?.label
  const isIcon = responsive ? Boolean(responsiveIcon) : Boolean(icon)
  const ArrowIcon = open ? FoldUpIcon : ExpandIcon
  color = color ?? isIcon ? 'secondary' : 'primary'

  return (
    <Menu
      isIcon={isIcon}
      isOpened={open}
      placement={placement}
      label={menuLabel}
      selected={selected}
      menuItems={menuItems}
      onClickItem={handleChange}
      isOpen={isOpen}
      endIcon={!isIcon && <ArrowIcon />}
      menuTitle={menuTitle}
      disabled={disabled}
      size={buttonSize}
      dense={dense}
      ariaLabel={ariaLabel}
      color={color}
    />
  )
}

export default FilterDropdown
